import React, { useState } from "react";
import { Camera, Loader2, CheckCircle2, XCircle, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { Section } from "@/features/workorders/Section";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export const PreinspectionPanel = ({ wo, preinspecciones, canUpload, onDone }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [observaciones, setObservaciones] = useState("");
  const [apta, setApta] = useState(true);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [broken, setBroken] = useState(() => new Set());
  const markBroken = (id) => setBroken((s) => new Set(s).add(id));

  const submit = async () => {
    setSaving(true);
    try {
      const form = new FormData();
      if (observaciones.trim()) form.append("observaciones", observaciones.trim());
      form.append("apta", String(apta));
      files.forEach((f) => form.append("files", f));
      await api.post(`/work-orders/${wo.id}/preinspection`, form);
      toast.success(t("app.saved"));
      setOpen(false);
      setObservaciones("");
      setApta(true);
      setFiles([]);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Section
        title={t("wo.preinspection.title")}
        testId="wo-preinspection"
        action={
          canUpload && (
            <Button size="sm" variant="outline" data-testid="wo-preinspection-register"
              onClick={() => setOpen(true)} className="h-7 px-2 text-[12px]">
              <Camera className="mr-1 h-3.5 w-3.5" /> {t("wo.preinspection.register")}
            </Button>
          )
        }
      >
        {!preinspecciones?.length ? (
          <p className="py-4 text-center text-[12.5px] text-ink-tertiary">{t("wo.preinspection.empty")}</p>
        ) : (
          <div className="space-y-3">
            {preinspecciones.map((p) => (
              <div key={p.id} data-testid={`wo-preinspection-${p.id}`} className="border border-line px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[12px] text-ink-tertiary">
                    {formatDateTime(p.fecha)} · {p.created_by_nombre || "—"}
                  </p>
                  {p.apta ? (
                    <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-state-ok">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {t("wo.preinspection.apta")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-state-critical">
                      <XCircle className="h-3.5 w-3.5" /> {t("wo.preinspection.no_apta")}
                    </span>
                  )}
                </div>
                {p.observaciones && <p className="mt-1.5 text-[12.5px] text-ink">{p.observaciones}</p>}
                {!!p.fotos?.length && (
                  <div className="mt-2 grid grid-cols-4 gap-1.5">
                    {p.fotos.map((f, i) => (
                      broken.has(f.key || i) ? (
                        <div key={f.key || i} className="flex aspect-square items-center justify-center bg-surface-sunken">
                          <ImageOff className="h-4 w-4 text-ink-tertiary" />
                        </div>
                      ) : (
                        <img key={f.key || i} src={f.thumb_url || f.url} alt={t("wo.evidence.types.preinspeccion")}
                          onError={() => markBroken(f.key || i)}
                          className="aspect-square w-full border border-line object-cover" />
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="wo-preinspection-dialog" className="max-w-[460px] rounded-surface">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{t("wo.preinspection.register")}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between border border-line px-3 py-2.5">
            <Label className="text-[12.5px]">{t("wo.preinspection.apta_label")}</Label>
            <Switch data-testid="wo-preinspection-apta-switch" checked={apta} onCheckedChange={setApta} />
          </div>
          <Textarea
            data-testid="wo-preinspection-observations"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            rows={3}
            placeholder={t("wo.preinspection.observations_ph")}
            className="text-[13px]"
          />
          <div>
            <Label className="text-[12.5px]">{t("wo.preinspection.photos")}</Label>
            <input
              type="file"
              multiple
              accept="image/*,.heic,.heif"
              data-testid="wo-preinspection-files"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="mt-1.5 block w-full text-[12.5px]"
            />
            <p className="mt-1 text-[11px] text-ink-tertiary">{t("wo.preinspection.photos_hint")}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-9 text-[13px]">
              {t("app.cancel")}
            </Button>
            <Button data-testid="wo-preinspection-submit" disabled={saving} onClick={submit} className="h-9 text-[13px]">
              {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t("app.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
